import { Component } from "modules/Component";

import selectTemplate from "./Select.hbs";

import "./select.scss";

interface OptionItem {
    value: string;
    textValue: string;
    disabled?: boolean;
}

export class Select extends Component<typeof Select, "labelTitle" | "labelChoose" | "labelSelected" | "labelOption" | "labelOptions" | "labelSearch"> {
    static readonly className = "js-select-component";

    private optionItems: OptionItem[] = [];
    private toggleButtonElement: HTMLButtonElement | null = null;

    constructor(selectElement: HTMLElement, index: number) {
        super(selectElement, index);

        if (!(selectElement instanceof HTMLSelectElement)) {
            throw new Error("Select component must be initialized with an HTMLSelectElement.");
        }

        const isSearchable = selectElement.attributes.getNamedItem("searchable") ? true : false;

        const { labelTitle, labelChoose, labelSelected, labelOptions, labelOption, labelSearch } = this.dataset;

        const selectData = {
            id: `select-${index}`,
            name: selectElement.name,
            multiple: selectElement.multiple,
            searchable: isSearchable,
            options: this.optionItems,
            labels: {
                labelTitle, labelChoose, labelSelected, labelOptions, labelOption, labelSearch
            }
        };

        Array.from(selectElement.options).forEach((option) => {
            this.optionItems.push({ value: option.value, textValue: option.textContent || "" })
        });

        this.rootElement = document.createElement("div");
        this.rootElement.classList.add("js-select-component", "select");
        this.rootElement.setAttribute("name", selectData.name);
        this.rootElement.id = selectData.id;
        selectElement.insertAdjacentElement("beforebegin", this.rootElement);

        this.rootElement.innerHTML = selectTemplate(selectData);
        this.toggleButtonElement = this.rootElement.querySelector<HTMLButtonElement>("[js-select-toggle]");

        selectElement.remove();

        this.addEventListeners();
    }

    disableAllOptions = () => {
        const optionItems = this.rootElement.querySelectorAll("[js-select-item]");
        optionItems.forEach((optionItem) => {
            const item = optionItem as HTMLElement;
            item.classList.add("disabled");
        });
    }

    enableAllOptions = () => {
        const optionItems = this.rootElement.querySelectorAll("[js-select-item]");
        optionItems.forEach((optionItem) => {
            const item = optionItem as HTMLElement;
            item.classList.remove("disabled");
        });
    }

    enableOption = (optionValue: string) => {
        const optionItem = this.rootElement.querySelector(`[js-select-item][data-select-value="${optionValue}"]`);
        const item = optionItem as HTMLElement;
        if (item) {
            item.classList.remove("disabled");
        }
    }

    updateLabel = (value: string, label: string) => {
        const optionItem = this.rootElement.querySelector<HTMLElement>(`[js-select-item][data-select-value="${value}"]`);
        const labelElement = optionItem?.querySelector("span");

        if (labelElement) {
            labelElement.innerText = label;
        }
    };

    resetLabels = () => {
        const optionItems = this.rootElement.querySelectorAll(`[js-select-item]`);

        optionItems?.forEach((optionItem) => {
            const item = optionItem as HTMLElement;
            const labelElement = item.querySelector("span") as HTMLElement;

            if (labelElement) {
                labelElement.innerText = item.getAttribute("data-select-text") || "";
            }
        });
    }

    getLabel = (value: string) => {
        const optionItem = this.rootElement.querySelector(`[js-select-item][data-select-value="${value}"]`);

        return optionItem?.getAttribute("data-select-text") || "";
    }

    private addEventListeners = () => {
        const toggleSelectButton = this.rootElement.querySelector("[js-select-toggle]");
        const optionItems = this.rootElement.querySelectorAll("[js-select-item]");
        const clearFilterButton = this.rootElement.querySelector("[js-clear-filter]");
        const searchInput = this.rootElement.querySelector("[js-select-search]");

        toggleSelectButton?.setAttribute("aria-expanded", "false");

        this.rootElement.addEventListener("click", (e: Event) => {
            const target = e.target as HTMLDivElement;

            if (target) {
                if (target.classList.contains("expanded")) {
                    this.toggleSelect(false);
                }
            }
        });

        toggleSelectButton?.addEventListener("click", (e: Event) => {
            e.preventDefault();
            this.toggleSelect();
        });

        optionItems.forEach((optionItem) => {
            // Click event
            optionItem.addEventListener("click", (e: Event) => {
                const target = e.target as HTMLLIElement;

                if (target) {
                    this.toggleOption(target);
                }
            });

            // Keydown event
            (optionItem as HTMLElement).addEventListener("keydown", (e: KeyboardEvent) => {
                const target = e.target as HTMLElement;
                if (e.key === " " || e.key === "Enter") {
                    e.preventDefault(); 
                    this.toggleOption(target);
                }
            });
        });

        clearFilterButton?.addEventListener("click", (e: Event) => {
            e.preventDefault();
            this.clearOptions();
        });

        // Listen for change on searchInput and reset filter if empty
        searchInput?.addEventListener("search", (e: Event) => {
            const target = e.target as HTMLInputElement;

            if (target.value.length > 0) {
                return;
            }

            const parent = target.closest("[js-select-panel]");

            if (parent) {
                const elements = parent.querySelectorAll("[js-select-item]");

                if (elements.length > 0) {
                    elements.forEach((element) => {
                        const item = element as HTMLElement;
                        item.style.display = "";
                    });
                }
            }
        });
        


        searchInput?.addEventListener("keyup", (e: Event) => {

            const target = e.target as HTMLInputElement;
            const parent = target.closest("[js-select-panel]");
            const userInputFilter = target.value.toUpperCase();

            if (parent) {
                const elements = parent.querySelectorAll("[js-select-item]");

                if (elements.length > 0) {
                    elements.forEach((element) => {
                        const item = element as HTMLElement;
                        const selectItem = item.querySelector("span");

                        if (selectItem !== null) {
                            const selectItemProcessed = selectItem.innerText.toUpperCase()

                            if (selectItemProcessed.indexOf(userInputFilter) > -1) {


                                item.style.display = "";
                            } else {
                                item.style.display = "none";
                            }
                        }
                    });
                }
            }
        });
    }

    toggleSelect = (expand?: boolean) => {
        const toExpand = expand || !this.rootElement.classList.contains("expanded");

        this.toggleButtonElement?.setAttribute("aria-expanded", toExpand.toString());

        if (toExpand) {
            this.rootElement.classList.add("expanded");
        } else {
            this.rootElement.classList.remove("expanded");
        }
    }

    toggleOption = (customOptionElement: HTMLElement) => {
        const parent = this.rootElement;
        const isMultiSelect = customOptionElement.classList.contains("multiple");

        if (isMultiSelect) {
            const isChecked = customOptionElement.classList.toggle("checked");
            customOptionElement.setAttribute("aria-checked", isChecked.toString());
        } else if (!customOptionElement.classList.contains("checked")) {
            const previousSelectedItem = parent.querySelector(".checked");

            if (previousSelectedItem) {
                previousSelectedItem.classList.remove("checked");
                previousSelectedItem.setAttribute("aria-checked", "false");
            }

            customOptionElement.classList.add("checked");
            customOptionElement.setAttribute("aria-checked", "true");
            this.toggleSelect(false);
        }

        this.emit("change", this.getSelectedValues());

        //Loop through selected items to get checked amount
        if (parent) {
            const items = parent.querySelectorAll("[js-select-item].checked");

            if (isMultiSelect) {
                this.setLabels(items.length, true);
            } else {
                this.setLabels(1, false);
            }
        }
    }

    clearOptions = () => {
        const checkedItems = this.rootElement.querySelectorAll("[js-select-item].checked");

        if (checkedItems.length > 0) {
            checkedItems.forEach((checkedItem) => {
                checkedItem.classList.remove("checked");
                this.setLabels(0, true);
                this.toggleSelect(false);
            });
        }

        this.emit("change", this.getSelectedValues());
    }

    getSelectedValues = () => {
        const selectedItems = this.rootElement.querySelectorAll<HTMLElement>("[js-select-item].checked");
        const selectedValues: string[] = [];

        const selectedItemsArray = Array.from(selectedItems);
        selectedItemsArray.forEach((selectedItem) => {
            const selectedValue = selectedItem.dataset.selectValue;

            if (selectedValue) {
                selectedValues.push(selectedValue);
            }
        });

        return selectedValues;
    }

    setSelectedValues = (selectedValues: string[]) => {
        const items = this.rootElement.querySelectorAll<HTMLElement>("[js-select-item]");
        const isMultiSelect = this.rootElement.classList.contains("multiple");
        let checkedAmount = 0;
        let selectedItem: HTMLElement | null = null;

        items.forEach((item) => {
            const itemValue = item.dataset.selectValue;

            if (itemValue && selectedValues.includes(itemValue)) {
                item.classList.add("checked");
                checkedAmount++;
            } else {
                item.classList.remove("checked");
            }
        });

        if (isMultiSelect) {
            this.setLabels(checkedAmount, true);
        } else {
            selectedItem = this.rootElement.querySelector(".checked span") as HTMLElement;

            if (selectedItem) {
                this.setLabels(1, false);
            } else {
                this.setLabels(0, false);
            }
        }
    }

    setLabels = (amount: number, isMultiSelect: boolean) => {
        const clearFilterButton = this.rootElement.querySelector("[js-clear-filter]") as HTMLElement;
        const labelContainer = this.rootElement.querySelector("[js-select-title]") as HTMLElement;
        const selectTitleLabel = labelContainer.getAttribute("data-label-title");
        const selectSelectedLabel = labelContainer.getAttribute("data-label-selected");
        const selectOptionLabel = labelContainer.getAttribute("data-label-option");
        const selectOptionsLabel = labelContainer.getAttribute("data-label-options");

        if (isMultiSelect) {
            switch (amount) {
                case 0:
                    if (selectTitleLabel) {
                        labelContainer.innerText = selectTitleLabel;
                    }
                    else {
                        labelContainer.innerText = "Select option";
                    }

                    clearFilterButton.style.display = "none";
                    break;
                case 1:
                    if (selectOptionLabel && selectSelectedLabel) {
                        labelContainer.innerText = amount + " " + selectOptionLabel + " " + selectSelectedLabel;
                    }
                    else {
                        labelContainer.innerText = amount + " option selected";
                    }

                    clearFilterButton.style.display = "flex";
                    break;
                default:
                    if (selectOptionsLabel && selectSelectedLabel) {
                        labelContainer.innerText = amount + " " + selectOptionsLabel + " " + selectSelectedLabel;
                    }
                    else {
                        labelContainer.innerText = amount + " options selected";
                    }

                    clearFilterButton.style.display = "flex";
            }
        }
        else {
            const selectedItem = this.rootElement.querySelector(".checked span");

            switch (amount) {
                case 0:
                    if (selectTitleLabel) {
                        labelContainer.innerText = selectTitleLabel;
                    }
                    else {
                        labelContainer.innerText = "Select option";
                    }

                    clearFilterButton.style.display = "none";
                    break;
                default:
                    if (selectedItem) {
                        const selectedTitle = selectedItem.textContent;

                        if (selectedTitle) {
                            labelContainer.innerText = selectedTitle;
                        }
                    }

                    clearFilterButton.style.display = "flex";
            }
        }
    }
}